import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { Play, Check, Clock, Calendar, Flame, BarChart3, Gift, Sparkles } from 'lucide-react';
import Header from '../../components/Header';
import SEOHead from '../../components/SEO/SEOHead';

const Dashboard: React.FC = () => {
  const { user, dailyRecord, currentDay, updateUserStats } = useAppStore();

  useEffect(() => {
    // 페이지 로드 시 통계 업데이트
    if (user) {
      updateUserStats({
        totalMinutesCompleted: user.stats.totalMinutesCompleted,
        streakCount: user.stats.streakCount,
        lastActiveDate: user.stats.lastActiveDate,
        achievements: user.stats.achievements,
        level: user.stats.level,
        experience: user.stats.experience,
        donationAmount: user.stats.donationAmount,
        donationCount: user.stats.donationCount
      });
    }
  }, [updateUserStats, user]);

  if (!user || !dailyRecord) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-primary">
        <div className="text-center animate-fade-in">
          <div className="logo w-16 h-16 mx-auto mb-6 rounded-2xl shadow-lg"></div>
          <div className="w-12 h-12 border-4 border-sparrow-200 border-t-sparrow-500 rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-neutral-600 font-medium">로딩 중...</p>
        </div>
      </div>
    );
  }
  const progressPercentage = Math.min((dailyRecord.totalMinutes / 90) * 100, 100);
  const isCompleted = dailyRecord.totalMinutes >= 90;

  return (
    <div className="min-h-screen bg-gradient-primary pb-24 animate-fade-in">
      <SEOHead 
        title={`Sparrow - Day ${currentDay} | ${user.name}님의 성장 대시보드`}
        description={`현재 Day ${currentDay}/90 진행 중. 오늘의 목표: ${user.physicalActivity.name} + ${user.mentalActivity.name}으로 90분 완성하기.`}
        type="app"
        url="https://sparrow-app.com/dashboard"
      />
      
      {/* 로고가 포함된 헤더 */}
      <Header 
        title={`안녕하세요, ${user.name}님! 👋`}
        subtitle="오늘도 성장의 하루를 시작해보세요"
      />

      {/* 현재 진행 상황 카드 */}
      <div className="px-mobile mb-6 sm:mb-8">
        <div className="card-elevated relative overflow-hidden">
          {/* 로고 워터마크 */}
          <div className="logo-watermark"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-sparrow-800">
                Day {currentDay} <span className="text-neutral-400 font-medium text-base sm:text-lg">/ 90</span>
              </h2>
              <div className="flex items-center space-x-2 bg-gradient-to-r from-accent-500 to-accent-600 text-white px-3 sm:px-4 py-2 rounded-full shadow-lg">
                <Flame size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span className="text-xs sm:text-sm font-bold">
                  {user.streakCount}일 연속
                </span>
              </div>
            </div>

            {/* 진행률 바 */}
            <div className="mb-6 sm:mb-8">
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <span className="text-sm font-medium text-neutral-700">오늘의 진행률</span>
                <span className="text-lg sm:text-xl font-bold text-sparrow-600">{Math.round(progressPercentage)}%</span>
              </div>
              <div className="progress-bar progress-bar-animated">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-neutral-500 mt-1 sm:mt-2">
                <span>{dailyRecord.totalMinutes}분</span>
                <span>90분</span>
              </div>
            </div>

            {/* 완료 상태 */}
            {isCompleted && (
              <div className="status-completed animate-scale-in">
                <div className="flex items-center">
                  <div className="bg-success-500 p-2 rounded-full mr-3 sm:mr-4">
                    <Check className="text-white" size={18} />
                  </div>
                  <div>
                    <p className="font-bold text-success-800 text-base sm:text-lg">오늘 목표 달성 완료!</p>
                    <p className="text-sm sm:text-base text-success-700">정말 훌륭해요! 내일도 화이팅! 🎉</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 오늘의 활동 */}
      <div className="px-mobile mb-6 sm:mb-8">
        <div className="flex items-center mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-bold text-sparrow-800">오늘의 활동</h3>
          <Sparkles className="ml-2 text-sparrow-500" size={18} />
        </div>
        
        <div className="space-y-3 sm:space-y-4">
          {/* 신체 활동 */}
          <div className="card-compact group active:scale-95 transition-transform duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 sm:space-x-4 flex-1">
                <div className="text-2xl sm:text-3xl bg-gradient-to-br from-accent-100 to-accent-200 p-2 sm:p-3 rounded-xl sm:rounded-2xl">
                  {user.physicalActivity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sparrow-800 text-base sm:text-lg truncate">
                    {user.physicalActivity.name}
                  </p>
                  <p className="text-xs sm:text-sm text-neutral-600">
                    {dailyRecord.physicalCompleted 
                      ? `✅ 완료 (${dailyRecord.physicalMinutes}분)`
                      : '⏱️ 아직 시작하지 않음'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 ml-3">
                {dailyRecord.physicalCompleted ? (
                  <div className="bg-success-500 p-2 sm:p-3 rounded-full">
                    <Check className="text-white" size={16} />
                  </div>
                ) : (
                  <Link
                    to="/timer"
                    state={{ mode: 'physical' }}
                    className="bg-gradient-to-r from-sparrow-500 to-sparrow-600 text-white p-2 sm:p-3 rounded-full 
                             hover:from-sparrow-600 hover:to-sparrow-700 transition-all duration-300 
                             shadow-sparrow hover:shadow-sparrow-lg active:scale-95"
                  >
                    <Play size={16} />
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* 정신 활동 */}
          <div className="card-compact group active:scale-95 transition-transform duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 sm:space-x-4 flex-1">
                <div className="text-2xl sm:text-3xl bg-gradient-to-br from-sparrow-100 to-sparrow-200 p-2 sm:p-3 rounded-xl sm:rounded-2xl">
                  {user.mentalActivity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sparrow-800 text-base sm:text-lg truncate">
                    {user.mentalActivity.name}
                  </p>
                  <p className="text-xs sm:text-sm text-neutral-600">
                    {dailyRecord.mentalCompleted 
                      ? `✅ 완료 (${dailyRecord.mentalMinutes}분)`
                      : '⏱️ 아직 시작하지 않음'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 ml-3">
                {dailyRecord.mentalCompleted ? (
                  <div className="bg-success-500 p-2 sm:p-3 rounded-full">
                    <Check className="text-white" size={16} />
                  </div>
                ) : (
                  <Link
                    to="/timer"
                    state={{ mode: 'mental' }}
                    className="bg-gradient-to-r from-sparrow-500 to-sparrow-600 text-white p-2 sm:p-3 rounded-full 
                             hover:from-sparrow-600 hover:to-sparrow-700 transition-all duration-300 
                             shadow-sparrow hover:shadow-sparrow-lg active:scale-95"
                  >
                    <Play size={16} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 통계 요약 */}
      <div className="px-mobile mb-6 sm:mb-8">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="card-compact text-center group active:scale-95 transition-transform duration-200">
            <div className="bg-gradient-to-br from-sparrow-100 to-sparrow-200 p-2 sm:p-3 rounded-full w-fit mx-auto mb-2 sm:mb-3">
              <Clock className="text-sparrow-600" size={20} />
            </div>
            <p className="text-xs sm:text-sm text-neutral-600 mb-1">총 시간</p>
            <p className="text-lg sm:text-2xl font-bold text-sparrow-700">
              {Math.floor(user.totalMinutesCompleted / 60)}h {user.totalMinutesCompleted % 60}m
            </p>
          </div>
          
          <div className="card-compact text-center group active:scale-95 transition-transform duration-200">
            <div className="bg-gradient-to-br from-sparrow-100 to-sparrow-200 p-2 sm:p-3 rounded-full w-fit mx-auto mb-2 sm:mb-3">
              <Calendar className="text-sparrow-600" size={20} />
            </div>
            <p className="text-xs sm:text-sm text-neutral-600 mb-1">전체 진행률</p>
            <p className="text-lg sm:text-2xl font-bold text-sparrow-700">
              {Math.round((currentDay / 90) * 100)}%
            </p>
          </div>
        </div>
      </div>

      {/* 빠른 액션 버튼들 */}
      <div className="px-mobile space-y-3 sm:space-y-4">
        <Link
          to="/progress"
          className="block w-full group"
        >
          <div className="card-compact active:scale-95 transition-all duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-br from-accent-100 to-accent-200 p-2 sm:p-3 rounded-xl">
                  <BarChart3 className="text-accent-600" size={18} />
                </div>
                <span className="font-semibold text-sparrow-800 text-sm sm:text-base">진행상황 자세히 보기</span>
              </div>
              <div className="text-sparrow-400 group-hover:text-sparrow-600 transition-colors text-lg">
                →
              </div>
            </div>
          </div>
        </Link>
        
        <Link
          to="/rewards"
          className="block w-full group"
        >
          <div className="card-compact active:scale-95 transition-all duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-br from-accent-100 to-accent-200 p-2 sm:p-3 rounded-xl">
                  <Gift className="text-accent-600" size={18} />
                </div>
                <span className="font-semibold text-sparrow-800 text-sm sm:text-base">보상 확인하기</span>
              </div>
              <div className="text-sparrow-400 group-hover:text-sparrow-600 transition-colors text-lg">
                →
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard; 